import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./scorecard.css";

const API_URL =
  "https://api.cricapi.com/v1/match_scorecard?apikey=0f32c689-8b39-48c4-8ac9-80912fdfe27e&id=4577bfcd-c130-4e42-8ed2-57de8e27a766";

function ExtrasRow({ extras }) {
  if (!extras) return null;
  const total =
    (extras?.r || 0) +
    (extras?.b || 0) +
    (extras?.lb || 0) +
    (extras?.wd || 0) +
    (extras?.nb || 0) +
    (extras?.p || 0);
  return (
    <div className="d-flex flex-wrap gap-2 small text-muted">
      <span>
        <strong>Extras:</strong> {total} (b {extras.b || 0}, lb {extras.lb || 0}, wd{" "}
        {extras.wd || 0}, nb {extras.nb || 0}, p {extras.p || 0})
      </span>
    </div>
  );
}

function InningsTable({ innings, open, onToggle }) {
  if (!innings) return null;
  return (
    <div className="card scorecard-card mb-3">
      <div
        className="card-header bg-dark text-white d-flex flex-column flex-md-row align-items-md-center justify-content-between"
        style={{ cursor: "pointer" }}
        onClick={onToggle}
      >
        <div className="fw-bold">
          {innings.inning}{" "}
          <span className="text-secondary">({innings.r}/{innings.w})</span>
        </div>
        <div className="mt-1 mt-md-0">
          <span className="badge bg-success me-2">
            {innings.r}/{innings.w}
          </span>
          <span className="badge bg-secondary">{innings.o} ov</span>
          <span className="ms-2">{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {open && (
        <div className="card-body p-0">
          {/* Batting */}
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light sticky-head">
                <tr>
                  <th>Batter</th>
                  <th>How Out</th>
                  <th className="text-end">R</th>
                  <th className="text-end">B</th>
                  <th className="text-end">4s</th>
                  <th className="text-end">6s</th>
                  <th className="text-end">SR</th>
                </tr>
              </thead>
              <tbody>
                {innings.batting?.map((b, i) => (
                  <tr key={i}>
                    <td className="fw-semibold">{b.batsman?.name}</td>
                    <td className="text-muted small">
                      {b.dismissal_text || b["dismissal-text"] || b.dismissal || "not out"}
                    </td>
                    <td className="text-end fw-bold">{b.r}</td>
                    <td className="text-end">{b.b}</td>
                    <td className="text-end">{b["4s"]}</td>
                    <td className="text-end">{b["6s"]}</td>
                    <td className="text-end">{b.sr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-3 py-2">
            <ExtrasRow extras={innings.extras} />
            <div className="small text-muted mt-1">
              <strong>Total:</strong> {innings.r}/{innings.w} in {innings.o} overs
            </div>
          </div>

          <hr className="my-2" />

          {/* Bowling */}
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light sticky-head">
                <tr>
                  <th>Bowler</th>
                  <th className="text-end">O</th>
                  <th className="text-end">R</th>
                  <th className="text-end">W</th>
                  <th className="text-end">Econ</th>
                </tr>
              </thead>
              <tbody>
                {innings.bowling?.map((bw, i) => (
                  <tr key={i}>
                    <td className="fw-semibold">{bw.bowler?.name}</td>
                    <td className="text-end">{bw.o}</td>
                    <td className="text-end">{bw.r}</td>
                    <td className="text-end fw-bold">{bw.w}</td>
                    <td className="text-end">{bw.eco}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Scorecard() {
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openInnings, setOpenInnings] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchScorecard() {
      try {
        const res = await fetch(API_URL);
        const json = await res.json();
        if (json.status === "success" && json.data) {
          setMatchData(json.data);
          setOpenInnings(
            json.data.scorecard.map((_, i) => i === 0) // open first innings
          );
        } else {
          setError("Failed to fetch match data.");
        }
      } catch (e) {
        console.error(e);
        setError("Error fetching data.");
      } finally {
        setLoading(false);
      }
    }
    fetchScorecard();
  }, []);

  const toggleInnings = (index) => {
    setOpenInnings((prev) =>
      prev.map((open, i) => (i === index ? !open : open))
    );
  };

  if (loading)
    return (
      <div className="container py-5 text-center text-white">
        <div className="spinner-border text-light" role="status"></div>
        <div className="mt-2">Loading live score...</div>
      </div>
    );

  if (error)
    return (
      <div className="container py-5 text-center text-danger">
        <strong>{error}</strong>
      </div>
    );

  const match = matchData;
  const innings = matchData.scorecard;

  return (
    <div className="container-fluid py-3 px-2">
      {/* Match header */}
      <div className="card border-0 shadow-sm mb-3 bg-dark text-white">
        <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div>
            <h5 className="mb-1">{match.name}</h5>
            <div className="small">
              {match.venue} • {match.date}
            </div>
          </div>
          <div className="text-md-end">
            <div className="small">
              <strong>Toss:</strong> {match.tossWinner} won the toss and chose to{" "}
              {match.tossChoice}
            </div>
            <div className="fw-semibold text-success mt-1">{match.status}</div>
          </div>
        </div>
      </div>

      {/* Innings */}
      {innings?.map((inn, i) => (
        <InningsTable
          key={i}
          innings={inn}
          open={openInnings[i]}
          onToggle={() => toggleInnings(i)}
        />
      ))}
    </div>
  );
}
